'use client';

import { useTranslation } from '@/lib/i18n';
import {
  CryptoFinancialMetric,
  CryptoFinancialPeriod,
  useCryptoFinancials,
} from '@/hooks/use-crypto-financials';
import TabBar from '@/components/common/TabBar';
import * as echarts from 'echarts';
import { useMemo, useState } from 'react';
import { formatCompactValue } from '@/lib/utils/number';
import EChart from '@/components/common/EChart';

interface CryptoFinancialProps {
  symbol: string;
  openInSidebar?: boolean;
}

export default function CryptoFinancial(props: CryptoFinancialProps) {
  const { symbol, openInSidebar = false } = props;
  const { t } = useTranslation();
  const tabItems = useMemo(
    () => [
      { label: t('Quarterly'), value: 'quarterly' },
      { label: t('Annual'), value: 'annual' },
    ],
    [t],
  );
  const [activeTab, setActiveTab] = useState<CryptoFinancialPeriod>('quarterly');
  const { data, isLoading } = useCryptoFinancials(symbol, activeTab);

  const handleChange = (tab: string) => {
    setActiveTab(tab as CryptoFinancialPeriod);
  };

  if (!data?.data?.length && !isLoading) {
    return null;
  }

  return (
    <div>
      <h2 className="py-5 text-lg font-medium">{t('Financials')}</h2>
      <TabBar
        value={activeTab}
        tabItems={tabItems}
        onChange={handleChange}
        warpperClassName="bg-[#1A1A1A]"
      />
      <FinancialChart data={data?.data || []} loading={isLoading} openInSidebar={openInSidebar} />
    </div>
  );
}

function FinancialChart({
  data,
  loading,
  openInSidebar = false,
}: {
  data: CryptoFinancialMetric[];
  loading: boolean;
  openInSidebar?: boolean;
}) {
  const { t } = useTranslation();
  const chartData = useMemo(() => [...data].reverse(), [data]);

  const option = useMemo<echarts.EChartsOption | null>(() => {
    if (!chartData.length) return null;

    const labels = chartData.map((item) => formatPeriodLabel(item.period_label));
    const expenses = chartData.map((item) => item.token_incentives);
    const fees = chartData.map((item) => item.fees);

    return {
      backgroundColor: 'transparent',
      color: ['#8B3DFF', '#20C987'],
      grid: {
        top: 40,
        left: 0,
        right: 0,
        bottom: 48,
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            color: 'rgba(255,255,255,0.04)',
          },
        },
        backgroundColor: 'rgba(17,17,17,0.95)',
        borderColor: '#303030',
        borderWidth: 1,
        textStyle: { color: '#fff' },
        valueFormatter: (value) => formatCompactValue(Number(value), '$'),
      },
      legend: { show: true, top: 0, left: 0, itemHeight: 6, textStyle: { color: '#ffffff' } },
      xAxis: {
        type: 'category',
        data: labels,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          color: '#757575',
          fontSize: 12,
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        splitNumber: 4,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#757575',
          fontSize: 12,
          formatter: (value: number) => formatCompactValue(value, '$'),
        },
        splitLine: {
          lineStyle: {
            color: '#242424',
            width: 1,
          },
        },
      },
      series: [
        {
          name: t('Expenses'),
          type: 'bar',
          data: expenses,
          barWidth: 20,
          barMinHeight: 3,
          barGap: '28%',
          itemStyle: {
            color: '#8B3DFF',
            borderRadius: [4, 4, 0, 0],
          },
        },
        {
          name: t('Fees'),
          type: 'bar',
          data: fees,
          barWidth: 20,
          barMinHeight: 3,
          itemStyle: {
            color: '#20C987',
            borderRadius: [4, 4, 0, 0],
          },
        },
      ],
    };
  }, [chartData, t]);

  return (
    <div className="relative mt-4 h-57.5 w-full bg-black">
      <EChart
        option={option}
        loading={loading}
        empty={!chartData.length}
        className="size-full"
        wrapperClassName="size-full"
        openInSidebar={openInSidebar}
        loadingFallback={<div className="absolute inset-0 animate-pulse rounded-lg bg-[#111]" />}
        emptyFallback={
          <div className="absolute inset-0 flex items-center justify-center text-sm text-[#757575]">
            {t('No financial data')}
          </div>
        }
      />
    </div>
  );
}

function formatPeriodLabel(periodLabel: string) {
  return periodLabel.replace(/\b(\d{4})\b/g, (_, year) => `FY${String(year).slice(-2)}`);
}
